import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {PermissionResult} from '../../../../../helpers/permissionHelpers';

export enum SynopsisQuickActionsEnum {
  FEEDBACK = 'feedback',
  UNPIN = 'unpin',
}

export interface SynopsisCellActionProps {
  actionId: SynopsisQuickActionsEnum;
  permissionResult?: PermissionResult;
  onClick: () => void;
}

const actionIcons: Record<SynopsisQuickActionsEnum, IconName> = {
  [SynopsisQuickActionsEnum.FEEDBACK]: 'thumbsdownSmall',
  [SynopsisQuickActionsEnum.UNPIN]: 'unpinOutlineSmall',
};

const actionLabels: Record<SynopsisQuickActionsEnum, ReactNode> = {
  [SynopsisQuickActionsEnum.FEEDBACK]: <Trans id="synopsis-action-feedback">Share feedback</Trans>,
  [SynopsisQuickActionsEnum.UNPIN]: <Trans id="synopsis-action-unpin">Unpin</Trans>,
};

const StyledButton = styled(ChromelessButton)`
  padding: 5px;
  border-radius: 6px;

  &:hover {
    background-color: ${(p) => p.theme.greys.shade40};
  }
`;

export const SynopsisQuickAction: FC<SynopsisCellActionProps> = ({actionId, permissionResult, onClick}) => {
  if (permissionResult && !permissionResult.isAllowed) {
    return (
      <TooltipCoordinator render={() => <PermissionDenied reason={permissionResult.reason} />}>
        <StyledButton isDisabled>
          <Icon name={actionIcons[actionId]} />
        </StyledButton>
      </TooltipCoordinator>
    );
  }

  return (
    <TooltipCoordinator render={() => actionLabels[actionId]}>
      <StyledButton
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <Icon name={actionIcons[actionId]} />
      </StyledButton>
    </TooltipCoordinator>
  );
};

const StyledActionsWrapperDiv = styled.div`
  position: relative;
`;

const StyledActionsDiv = styled.div<{$right: number; $bottom: number}>`
  position: absolute;
  display: flex;
  flex-direction: row;
  right: ${(p) => p.$right}px;
  bottom: ${(p) => p.$bottom}px;

  border-radius: 6px;
  background-color: ${(p) => p.theme.greys.white};
  ${(p) => boxShadow(p.theme, `0 0 8px 0 ${p.theme.alphas.gray10}`)};

  visibility: hidden;

  ${StyledActionsWrapperDiv}:hover & {
    visibility: visible;
  }
`;

interface SynopsisQuickActionsProps {
  actions?: ReactNode;
  children: ReactNode;
  right: number;
  bottom: number;
}

export const SynopsisQuickActions: FC<SynopsisQuickActionsProps> = ({actions, right, bottom, children}) => (
  <StyledActionsWrapperDiv>
    {children}
    {actions && (
      <StyledActionsDiv $right={right} $bottom={bottom}>
        {actions}
      </StyledActionsDiv>
    )}
  </StyledActionsWrapperDiv>
);
